import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from '../../../../firebaseConfig';
import { Button, Upload, message, Modal, Switch } from 'antd';
import { DeleteOutlined, UploadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import './Banner.less';

const Banner = () => {
    const [banners, setBanners] = useState<{ id: string, bannerPhotoUrl: string }[]>([]);
    const [newBanner, setNewBanner] = useState<File | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        fetchBanners();
        fetchBannerSettings();
    }, []);

    const fetchBanners = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "banner-photos"));
            const bannerData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as { id: string, bannerPhotoUrl: string }[];
            console.log("Fetched banners: ", bannerData); // Debug log
            setBanners(bannerData);
        } catch (error) {
            console.error("Error fetching banners: ", error);
        }
    };

    const fetchBannerSettings = async () => {
        try {
            const bannerSettingsDoc = await getDoc(doc(db, "configurations", "banner-settings"));
            if (bannerSettingsDoc.exists()) {
                setIsEnabled(bannerSettingsDoc.data().isEnable);
            }
        } catch (error) {
            console.error("Error fetching banner settings: ", error);
        }
    };

    const handleUpload = async () => {
        if (!newBanner) return;
        try {
            const storageRef = ref(storage, `banner-photos/${newBanner.name}`);
            const snapshot = await uploadBytes(storageRef, newBanner);
            const bannerPhotoUrl = await getDownloadURL(snapshot.ref);

            const newBannerDoc = {
                bannerPhotoUrl
            };

            await addDoc(collection(db, "banner-photos"), newBannerDoc);
            setNewBanner(null);
            fetchBanners();
            setIsModalOpen(false);
            message.success("Banner uploaded successfully!");
        } catch (error) {
            console.error("Error uploading banner: ", error);
            message.error("Error uploading banner!");
        }
    };

    const handleDeleteBanner = async (bannerId: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this banner?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const bannerDoc = doc(db, "banner-photos", bannerId);
                    await deleteDoc(bannerDoc);
                    fetchBanners();
                    message.success("Banner deleted successfully!");
                } catch (error) {
                    console.error("Error deleting banner: ", error);
                    message.error("Error deleting banner!");
                }
            },
        });
    };

    const handleToggleEnable = async (checked: boolean) => {
        try {
            await updateDoc(doc(db, "configurations", "banner-settings"), {
                isEnable: checked
            });
            setIsEnabled(checked);
            message.success(`Banners are now ${checked ? 'enabled' : 'disabled'}`);
        } catch (error) {
            console.error("Error updating banner settings: ", error);
            message.error("Error updating banner settings!");
        }
    };

    const renderCustomArrowPrev = (onClickHandler: () => void, hasPrev: boolean, label: string) =>
        hasPrev && (
            <Button
                className="custom-arrow custom-arrow-prev"
                onClick={onClickHandler}
                icon={<LeftOutlined />}
            />
        );

    const renderCustomArrowNext = (onClickHandler: () => void, hasNext: boolean, label: string) =>
        hasNext && (
            <Button
                className="custom-arrow custom-arrow-next"
                onClick={onClickHandler}
                icon={<RightOutlined />}
            />
        );

    return (
        <div className="banner-container">
            <div className="banner-header">
                <h2>Banner List</h2>
                <Switch 
                    checked={isEnabled} 
                    onChange={handleToggleEnable} 
                    checkedChildren="Enabled" 
                    unCheckedChildren="Disabled" 
                />
            </div>
            <Button className="banner-add-banner-btn" type="primary" onClick={() => setIsModalOpen(true)}>Add New Banner</Button>
            <div className="banner-carousel-wrapper">
                <Carousel 
                    showArrows={true}
                    dynamicHeight={true}
                    renderArrowPrev={renderCustomArrowPrev}
                    renderArrowNext={renderCustomArrowNext}
                >
                    {banners.map(banner => (
                        <div key={banner.id} className="banner-item">
                            <img src={banner.bannerPhotoUrl} alt="Banner" className="banner-image" />
                            <Button
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteBanner(banner.id)}
                                shape="circle"
                                className="delete-button"
                            />
                        </div>
                    ))}
                </Carousel>
            </div>

            <Modal
                title="Upload New Banner"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleUpload}
                okText="Upload"
                okButtonProps={{ disabled: !newBanner }}
            >
                <Upload
                    beforeUpload={file => {
                        setNewBanner(file);
                        return false;
                    }}
                    onRemove={() => setNewBanner(null)}
                    fileList={newBanner ? [{ ...newBanner, uid: newBanner.name }] : []}
                    listType="picture"
                    maxCount={1}
                >
                    <Button icon={<UploadOutlined />}>Select Banner</Button>
                </Upload>
            </Modal>
        </div>
    );
};

export default Banner;